// ============================================
// Path: /frontend/src/pages/ContactPage.jsx
// Purpose: Contact form for users to send messages to support
// UPDATED: Mobile-first responsive styling applied, WhatsApp field added
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState } from 'react';
import { contactsAPI } from '../api/contacts.api';
import { useToast } from '../hooks/useToast';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, Loader2 } from 'lucide-react';

// ========================================
// MAIN COMPONENT
// ========================================
export default function ContactPage() {
  console.log('🎨 [ContactPage] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('👤 [ContactPage] Form submit initiated', { name, email, whatsapp, subject });

    if (!name || !email || !subject || !message) {
      toast({ 
        variant: 'destructive', 
        title: 'Please fill out all required fields.' 
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔵 [ContactPage] Submitting contact form...', { 
        name, 
        email,
        whatsapp,
        subject 
      });

      const data = await contactsAPI.submitContact({
        name,
        email,
        whatsapp,
        subject,
        message
      });

      console.log('✅ [ContactPage] Message sent successfully:', data);

      toast({ 
        title: 'Message Sent!', 
        description: "We'll get back to you shortly." 
      });

      // Reset form
      console.log('🔄 [ContactPage] Resetting form');
      setName('');
      setEmail('');
      setWhatsapp('');
      setSubject('');
      setMessage('');
    } catch (error) {
      console.error('❌ [ContactPage] Submission error:', error);
      toast({ 
        variant: 'destructive', 
        title: 'Error', 
        description: error.response?.data?.message || 'Could not send your message. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        {/* Page Header */}
        <div className="text-left mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">
            Contact Us
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
            We'd love to hear from you. Here's how you can reach us.
          </p>
        </div>

        {/* Content Grid */}
        <div className="space-y-6 sm:space-y-8">
          {/* Contact Form Section */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-3 sm:mb-4 text-foreground">
              Get in Touch
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <Input
                placeholder="Your Name *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="text-xs sm:text-sm md:text-base"
              />
              <Input
                type="email"
                placeholder="Your Email *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="text-xs sm:text-sm md:text-base"
              />
              <Input
                type="tel"
                placeholder="WhatsApp Number (Optional)"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className="text-xs sm:text-sm md:text-base"
              />
              <Input
                placeholder="Subject *"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                className="text-xs sm:text-sm md:text-base"
              />
              <Textarea
                placeholder="Your Message *"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                className="text-xs sm:text-sm md:text-base"
              />
              <Button 
                type="submit" 
                size="lg" 
                disabled={isSubmitting}
                className="w-full sm:w-auto text-xs sm:text-sm md:text-base"
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                ) : null}
                Send Message
              </Button>
            </form>
          </section>

          {/* Contact Information Section */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-3 sm:mb-4 text-foreground">
              Our Information
            </h2>
            
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-xs sm:text-sm md:text-base text-foreground">Email</h3>
                  <p className="text-xs sm:text-sm md:text-base text-foreground/80">
                    support@motohomefinder.com
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-xs sm:text-sm md:text-base text-foreground">Phone</h3>
                  <p className="text-xs sm:text-sm md:text-base text-foreground/80">0735880907</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
          <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground text-center">
            Last updated: December 2025
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}